import { useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useT } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { filterSources, refreshMode, type SourceRow } from "./library";

export interface SourcesData {
  as_of: string;
  kinds: Record<string, string>;
  areas: string[];
  sources: SourceRow[];
}

const MODE_TONE = { automatic: "ok", live: "info", manual: "warn", none: "" } as const;
const MODE_LABEL: Record<"automatic" | "live" | "manual", MessageKey> = {
  automatic: "library.sources.mode.automatic",
  live: "library.sources.mode.live",
  manual: "library.sources.mode.manual",
};

/** Filterable table of every dataset, grouped by area. */
export function SourcesView({ data }: { data: SourcesData }) {
  const t = useT();
  const [q, setQ] = useState("");
  const [area, setArea] = useState("");
  const [kind, setKind] = useState("");
  const rows = useMemo(() => filterSources(data.sources, q, area, kind), [data.sources, q, area, kind]);
  const areas = data.areas.filter((a) => rows.some((r) => r.area === a));
  const counts = Object.keys(data.kinds).map((k) => [k, data.sources.filter((s) => s.kind === k).length] as const);
  return (
    <div className="stack">
      <div className="lib-stats">
        <div className="lib-stat"><b>{data.sources.length}</b><span>{t("library.sources.datasets", { count: data.sources.length })}</span></div>
        {counts.map(([k, n]) => (
          <div className="lib-stat" key={k}><b>{n}</b><span>{data.kinds[k].toLowerCase()}</span></div>
        ))}
      </div>
      <div className="lib-filters" role="search">
        <label>
          {t("library.filter.search")}
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("library.sources.searchPlaceholder")} dir="auto" />
        </label>
        <label>
          {t("library.filter.area")}
          <select value={area} onChange={(e) => setArea(e.target.value)}>
            <option value="">{t("library.filter.allAreas")}</option>
            {data.areas.map((a) => (
              <option key={a}>{a}</option>
            ))}
          </select>
        </label>
        <label>
          {t("library.sources.source")}
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">{t("library.filter.allKinds")}</option>
            {Object.entries(data.kinds).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </label>
        <span className="lib-count" aria-live="polite">{t("library.filter.count", { shown: rows.length, total: data.sources.length })}</span>
      </div>
      <div className="lib-table-wrap tall">
        <table className="lib-table" style={{ minWidth: 1320 }}>
          <thead>
            <tr>
              <th>{t("library.sources.th.dataset")}</th>
              <th>{t("library.sources.th.provider")}</th>
              <th>{t("library.sources.th.usedFor")}</th>
              <th>{t("library.sources.th.refresh")}</th>
              <th>{t("library.sources.th.licence")}</th>
              <th>{t("library.sources.th.code")}</th>
            </tr>
          </thead>
          <tbody>
            {areas.map((a) => (
              <AreaRows key={a} area={a} rows={rows.filter((r) => r.area === a)} kinds={data.kinds} />
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="lib-muted">{t("library.sources.noMatch")}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="lib-small">
        <FormattedMessage
          id="library.sources.legend"
          values={{
            auto: (c) => <span className="lib-pill ok">{c}</span>,
            live: (c) => <span className="lib-pill info">{c}</span>,
            manual: (c) => <span className="lib-pill warn">{c}</span>,
            date: data.as_of,
          }}
        />
      </p>
    </div>
  );
}

function AreaRows({ area, rows, kinds }: { area: string; rows: SourceRow[]; kinds: Record<string, string> }) {
  const t = useT();
  return (
    <>
      <tr className="lib-group-row">
        <td colSpan={6}>{area}</td>
      </tr>
      {rows.map((r) => {
        const mode = refreshMode(r.refresh);
        return (
          <tr key={r.name}>
            <td className="nm" style={{ minWidth: 220 }}>
              {r.name}
              <div style={{ marginTop: 4 }}>
                <span className={`lib-kind ${r.kind}`}>{kinds[r.kind] ?? r.kind}</span>
              </div>
              <div className="lib-small" style={{ fontWeight: 400, marginTop: 4 }}>{r.what}</div>
            </td>
            <td style={{ minWidth: 170 }}>{r.provider}</td>
            <td className="wide">
              {r.used_for}
              {r.notes && <div className="lib-small" style={{ marginTop: 4 }}>{r.notes}</div>}
            </td>
            <td style={{ minWidth: 230 }}>
              {mode !== "none" && <span className={`lib-pill ${MODE_TONE[mode]}`}>{t(MODE_LABEL[mode])}</span>} {r.refresh}
              {r.schedule && r.schedule !== "—" && <div className="lib-small" style={{ marginTop: 4 }}>{r.schedule}</div>}
            </td>
            <td className="basis">{r.licence}</td>
            <td className="where">{r.code}</td>
          </tr>
        );
      })}
    </>
  );
}
